import React, { useState } from 'react'
import {Mail, Phone, MapPin, Send, CheckCircle} from 'lucide-react'
import { lumi } from '@/lib/lumi'

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    type: 'general-inquiry' as 'feedback' | 'general-inquiry' | 'support' | 'partnership',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
    setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.subject.trim() || !formData.message.trim()) {
      setError('Please fill in all required fields.')
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address.')
      return
    }

    setIsSubmitting(true)

    try {
      const now = new Date().toISOString()
      await lumi.entities.contact_submissions.create({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        type: formData.type,
        status: 'new',
        creator: 'anonymous',
        createdAt: now,
        updatedAt: now
      })

      setSubmitted(true)
      setFormData({
        name: '',
        email: '',
        subject: '',
        type: 'general-inquiry',
        message: ''
      })
    } catch (err) {
      setError('Failed to submit your message. Please try again.')
      console.error('Submission error:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#4A90E2] to-[#5DADE2] text-white py-16" aria-labelledby="contact-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 id="contact-heading" className="text-4xl md:text-5xl font-bold mb-4">Get in Touch</h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            Have questions about anxiety management, our resources, or need support? We're here to help.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-[#4A90E2] bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" aria-hidden="true">
                      <Mail className="w-6 h-6 text-[#4A90E2]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                      <a href="mailto:support@anxietyusa.com" className="text-gray-600 hover:text-[#4A90E2] transition-colors">support@anxietyusa.com</a>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-[#48C9B0] bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" aria-hidden="true">
                      <Phone className="w-6 h-6 text-[#48C9B0]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Crisis Support</h3>
                      <p className="text-gray-600">National Suicide Prevention Lifeline</p>
                      <a href="tel:988" className="text-[#4A90E2] font-semibold hover:underline">988</a>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-[#5DADE2] bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" aria-hidden="true">
                      <MapPin className="w-6 h-6 text-[#5DADE2]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Location</h3>
                      <p className="text-gray-600">Serving all 50 states across the USA</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6" role="alert">
                <h3 className="font-semibold text-amber-900 mb-2">⚠️ Emergency Notice</h3>
                <p className="text-sm text-amber-800">
                  If you're experiencing a mental health emergency, please call 988 (Suicide & Crisis Lifeline) or go to your nearest emergency room immediately.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent Successfully!</h3>
                    <p className="text-gray-600 mb-6">
                      Thank you for reaching out. We'll review your message and respond within 24-48 hours.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="text-[#4A90E2] font-semibold hover:text-[#48C9B0] transition-colors"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
                    <form onSubmit={handleSubmit} className="space-y-6" aria-label="Contact form">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                            Full Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent transition-all"
                            placeholder="John Doe"
                            required
                            aria-required="true"
                          />
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                            Email Address <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent transition-all"
                            placeholder="john@example.com"
                            required
                            aria-required="true"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="type" className="block text-sm font-semibold text-gray-700 mb-2">
                            Inquiry Type <span className="text-red-500">*</span>
                          </label>
                          <select
                            id="type"
                            name="type"
                            value={formData.type}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent transition-all"
                            required
                            aria-required="true"
                          >
                            <option value="general-inquiry">General Inquiry</option>
                            <option value="feedback">Feedback</option>
                            <option value="support">Support Request</option>
                            <option value="partnership">Partnership</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                            Subject <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent transition-all"
                            placeholder="How can we help?"
                            required
                            aria-required="true"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                          Message <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={6}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent transition-all resize-none"
                          placeholder="Please share your thoughts, questions, or concerns..."
                          required
                          aria-required="true"
                        ></textarea>
                      </div>

                      {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm" role="alert" aria-live="assertive">
                          {error}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-[#4A90E2] text-white px-6 py-4 rounded-lg font-semibold hover:bg-[#3A7BC8] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        aria-label={isSubmitting ? 'Sending message...' : 'Send message'}
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            Sending...
                          </>
                        ) : (
                          <>
                            Send Message
                            <Send className="ml-2 w-5 h-5" aria-hidden="true" />
                          </>
                        )}
                      </button>
                    </form>
                  </>
                )}
              </div>

              <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="font-semibold text-[#4A90E2] mb-2">📋 Response Time</h3>
                <p className="text-sm text-gray-700">
                  We typically respond to inquiries within 24-48 hours during business days. For urgent mental health support, please use the crisis resources listed above.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50" aria-labelledby="faq-heading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">Is this service confidential?</h3>
              <p className="text-gray-600">
                Yes. Your inquiries are stored securely and only accessible to our team. We never share your information with third parties.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">Can you provide medical advice or therapy?</h3>
              <p className="text-gray-600">
                No. Anxiety USA is an educational resource. We cannot provide medical advice, diagnosis, or therapy. Please consult a licensed mental health professional for personalized care.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">How can I find a therapist?</h3>
              <p className="text-gray-600">
                Visit our <a href="/therapy" className="text-[#4A90E2] hover:underline">Therapy Guide</a> page for comprehensive information on finding a qualified therapist in your area.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
